import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Copy, Check, ArrowRight, Link2, Shield, Clock,
  Users, MessageSquare, Bot, User, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsAccountLinking() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const chatMessages = [
    { from: 'user', text: 'link mystore.com' },
    { from: 'bot', text: 'Account linking initiated for mystore.com\n\nA 6-digit verification code has been sent to the admin email on file (a]***@mystore.com).\n\nPlease enter the code here within 10 minutes.' },
    { from: 'user', text: '847291' },
    { from: 'bot', text: 'Account linked successfully!\n\nYour Telegram identity is now connected to the mystore.com merchant account.\n\nYou now have full access to all Commerce Index commands.\n\nTry /pulse to get started.' },
  ];

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Account Linking
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Account Linking
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Connect your messaging identity to your Commerce Index merchant account using a simple
          OTP-based verification flow. Works on any connected messaging platform.
        </p>
      </div>

      {/* How It Works */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          How It Works
        </h2>
        <div className="space-y-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#3EF1C7]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#3EF1C7] font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Send the Link Command</h3>
                  <p className="text-[#94A3B8] text-sm">
                    In any connected messaging channel (Telegram, WhatsApp, Slack, or iMessage), send
                    <code className="text-[#3EF1C7] mx-1">link yourdomain.com</code> where <code className="text-[#3EF1C7]">yourdomain.com</code> is
                    the domain associated with your Commerce Index merchant account.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sky-400 font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Receive the OTP via Email</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Commerce Index generates a 6-digit one-time password and sends it to the admin email
                    address registered for that domain. Check your inbox (and spam folder) for an email
                    from <code className="text-[#3EF1C7]">noreply@commerceindex.com</code>.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-400 font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Enter the OTP in Chat</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Type the 6-digit code directly in the same chat where you sent the link command.
                    No prefix needed — just the code. For example: <code className="text-[#3EF1C7]">847291</code>.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-400 font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Account Linked</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Once verified, your messaging identity is permanently connected to your merchant account.
                    You can now use all CI commands from this messaging platform. The link persists across
                    sessions — you will not need to re-link unless you explicitly unlink.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Chat Mockup */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Conversation Flow
        </h2>
        <div className="rounded-xl border border-[#22304A] bg-[#0B1120] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#22304A] flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#3EF1C7]/20 flex items-center justify-center">
              <Bot className="w-4 h-4 text-[#3EF1C7]" />
            </div>
            <div>
              <div className="text-sm font-semibold text-white">Commerce Index Bot</div>
              <div className="text-xs text-[#64748B]">Account Linking Flow</div>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {chatMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm whitespace-pre-line ${
                  msg.from === 'user'
                    ? 'bg-[#3EF1C7] text-[#0B1120] rounded-br-md'
                    : 'bg-[#1E293B] text-[#E2E8F0] rounded-bl-md'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Security
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">OTP Expiry</h3>
                  <p className="text-sm text-[#94A3B8]">
                    Verification codes expire after <strong className="text-white">10 minutes</strong>.
                    After expiry, you must start the linking process again with a new <code className="text-[#3EF1C7]">link</code> command.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">Rate Limited</h3>
                  <p className="text-sm text-[#94A3B8]">
                    Maximum <strong className="text-white">3 verification attempts</strong> per OTP.
                    After 3 failed attempts, the code is invalidated and you must request a new one.
                    Limit of 5 link requests per hour per messaging identity.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">Multi-User Support</h3>
                  <p className="text-sm text-[#94A3B8]">
                    Multiple team members can link their own messaging accounts to the same merchant account.
                    Each person links independently using their own messaging identity. All linked users
                    share the same merchant data but maintain individual notification preferences.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Link2 className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">Multi-Platform</h3>
                  <p className="text-sm text-[#94A3B8]">
                    One merchant account can be linked to multiple messaging platforms simultaneously.
                    Link from Telegram, WhatsApp, Slack, and iMessage — all will have access to the
                    same data and commands. Cross-platform threads sync automatically.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* API Details */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Technical Details
        </h2>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(`# Account linking API flow (internal)

# Step 1: User sends "link mystore.com"
# CI resolves the domain to a merchant_id
POST /api/v1/auth/link/initiate
{
  "platform": "telegram",
  "platform_user_id": "tg_123456789",
  "domain": "mystore.com"
}
# Response: { "status": "otp_sent", "email_hint": "a***@mystore.com", "expires_in": 600 }

# Step 2: User enters OTP
POST /api/v1/auth/link/verify
{
  "platform": "telegram",
  "platform_user_id": "tg_123456789",
  "domain": "mystore.com",
  "otp": "847291"
}
# Response: { "status": "linked", "merchant_id": "merch_abc123", "user_role": "admin" }

# Unlinking
POST /api/v1/auth/link/revoke
{
  "platform": "telegram",
  "platform_user_id": "tg_123456789"
}
# Response: { "status": "unlinked" }`, 'link-api')}
            className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
          >
            {copiedCode === 'link-api' ? (
              <Check className="w-4 h-4 text-[#3EF1C7]" />
            ) : (
              <Copy className="w-4 h-4 text-[#64748B]" />
            )}
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Account linking API flow (internal)

# Step 1: User sends "link mystore.com"
# CI resolves the domain to a merchant_id
POST /api/v1/auth/link/initiate
{
  "platform": "telegram",
  "platform_user_id": "tg_123456789",
  "domain": "mystore.com"
}
# Response: { "status": "otp_sent", "email_hint": "a***@mystore.com", "expires_in": 600 }

# Step 2: User enters OTP
POST /api/v1/auth/link/verify
{
  "platform": "telegram",
  "platform_user_id": "tg_123456789",
  "domain": "mystore.com",
  "otp": "847291"
}
# Response: { "status": "linked", "merchant_id": "merch_abc123", "user_role": "admin" }

# Unlinking
POST /api/v1/auth/link/revoke
{
  "platform": "telegram",
  "platform_user_id": "tg_123456789"
}
# Response: { "status": "unlinked" }`}</code></pre>
        </div>
      </section>

      {/* What's Next? */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          What's Next?
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/commands">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Messaging Commands <ArrowRight className="inline w-4 h-4" /></h3>
                <p className="text-sm text-[#94A3B8]">Full command reference for all channels</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/daily-briefs">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Daily Briefs <ArrowRight className="inline w-4 h-4" /></h3>
                <p className="text-sm text-[#94A3B8]">Set up automated daily summaries and alerts</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
